import { useCallback, useEffect, useMemo, useState } from 'react';
const DEFAULT_STATE = {
    status: 'not_started',
    currentStep: 0,
    lastUpdatedAt: new Date(0).toISOString(),
};
function getStorageKey(userId) {
    return `flowdoc:walkthrough:first-run:v1:${userId}`;
}
function readState(storageKey) {
    if (typeof window === 'undefined')
        return DEFAULT_STATE;
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw)
            return DEFAULT_STATE;
        const parsed = JSON.parse(raw);
        const status = parsed.status === 'not_started' ||
            parsed.status === 'in_progress' ||
            parsed.status === 'skipped' ||
            parsed.status === 'complete'
            ? parsed.status
            : 'not_started';
        const currentStep = typeof parsed.currentStep === 'number' && Number.isFinite(parsed.currentStep)
            ? Math.max(0, Math.floor(parsed.currentStep))
            : 0;
        const lastUpdatedAt = typeof parsed.lastUpdatedAt === 'string'
            ? parsed.lastUpdatedAt
            : DEFAULT_STATE.lastUpdatedAt;
        return { status, currentStep, lastUpdatedAt };
    }
    catch {
        return DEFAULT_STATE;
    }
}
function writeState(storageKey, state) {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(state));
    }
    catch {
        // Ignore storage failures (private mode, quota).
    }
}
export function useWelcomeWalkthrough(userId, options) {
    const { totalSteps } = options;
    const storageKey = useMemo(() => getStorageKey(userId), [userId]);
    const [state, setState] = useState(() => readState(storageKey));
    useEffect(() => {
        setState(readState(storageKey));
    }, [storageKey]);
    useEffect(() => {
        writeState(storageKey, state);
    }, [storageKey, state]);
    const start = useCallback(() => {
        setState({
            status: 'in_progress',
            currentStep: 0,
            lastUpdatedAt: new Date().toISOString(),
        });
    }, []);
    const next = useCallback(() => {
        setState((prev) => {
            const nextStep = prev.currentStep + 1;
            if (nextStep >= totalSteps) {
                return {
                    status: 'complete',
                    currentStep: totalSteps - 1,
                    lastUpdatedAt: new Date().toISOString(),
                };
            }
            return {
                status: 'in_progress',
                currentStep: nextStep,
                lastUpdatedAt: new Date().toISOString(),
            };
        });
    }, [totalSteps]);
    const previous = useCallback(() => {
        setState((prev) => ({
            ...prev,
            status: 'in_progress',
            currentStep: Math.max(0, prev.currentStep - 1),
            lastUpdatedAt: new Date().toISOString(),
        }));
    }, []);
    const skip = useCallback(() => {
        setState((prev) => ({
            status: 'skipped',
            currentStep: prev.currentStep,
            lastUpdatedAt: new Date().toISOString(),
        }));
    }, []);
    const complete = useCallback(() => {
        setState(() => ({
            status: 'complete',
            currentStep: Math.max(0, totalSteps - 1),
            lastUpdatedAt: new Date().toISOString(),
        }));
    }, [totalSteps]);
    const replay = useCallback(() => {
        setState({
            status: 'in_progress',
            currentStep: 0,
            lastUpdatedAt: new Date().toISOString(),
        });
    }, []);
    const goToStep = useCallback((stepIndex) => {
        const n = Math.max(0, Math.min(totalSteps - 1, Math.floor(stepIndex)));
        setState((prev) => ({
            ...prev,
            status: 'in_progress',
            currentStep: n,
            lastUpdatedAt: new Date().toISOString(),
        }));
    }, [totalSteps]);
    const reset = useCallback(() => {
        setState(DEFAULT_STATE);
    }, []);
    return {
        status: state.status,
        currentStep: state.currentStep,
        totalSteps,
        state,
        start,
        next,
        previous,
        goToStep,
        skip,
        complete,
        replay,
        reset,
    };
}
